import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount}
  const [couponBusy, setCouponBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "United States",
    phone: "",
    notes: "",
  });

  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 9.99;
  const tax = subtotal * 0.0825;
  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal + shipping + tax - discount);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode, subtotal });
      setCoupon(data);
      toast.success(`Coupon applied — $${data.discount.toFixed(2)} off`);
    } catch (e) { toast.error(formatApiError(e)); setCoupon(null); }
    finally { setCouponBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: {
          full_name: form.full_name, email: form.email, phone: form.phone,
          address_line1: form.address_line1, address_line2: form.address_line2,
          city: form.city, state: form.state, postal_code: form.postal_code, country: form.country,
        },
        notes: form.notes,
        coupon_code: coupon?.code || null,
      });
      // Create Stripe Checkout Session and redirect
      const { data: session } = await api.post("/checkout/session", {
        order_id: order.order_id,
        origin_url: window.location.origin,
      });
      clearCart();
      window.location.href = session.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rh-overline mb-3">Step 02 / 03</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-10">Checkout</h1>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-8">
            <section>
              <h2 className="font-display font-bold text-2xl mb-5">Shipping Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required value={form.full_name} onChange={set("full_name")} testid="full-name" />
                <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="email" />
                <Field label="Phone" required value={form.phone} onChange={set("phone")} testid="phone" />
                <Field label="Country" required value={form.country} onChange={set("country")} testid="country" />
                <Field className="sm:col-span-2" label="Address line 1" required value={form.address_line1} onChange={set("address_line1")} testid="addr1" />
                <Field className="sm:col-span-2" label="Address line 2 (optional)" value={form.address_line2} onChange={set("address_line2")} testid="addr2" />
                <Field label="City" required value={form.city} onChange={set("city")} testid="city" />
                <Field label="State / Region" required value={form.state} onChange={set("state")} testid="state" />
                <Field label="Postal code" required value={form.postal_code} onChange={set("postal_code")} testid="postal" />
              </div>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-5">Order notes</h2>
              <textarea data-testid="notes-input" value={form.notes} onChange={set("notes")} rows={3} placeholder="Special delivery instructions…" className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
            </section>

            <section className="border border-[#0a0a0a] p-5 bg-[#f5f5f5]">
              <h2 className="font-display font-bold text-lg mb-2">Payment by Stripe</h2>
              <p className="text-sm text-[#4a4a4a]">
                Secure checkout powered by Stripe. You will be redirected to Stripe to pay. (Test mode — use card <code className="font-mono">4242 4242 4242 4242</code>, any future expiry, any CVC.)
              </p>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-[#0a0a0a] p-6 space-y-4 sticky top-32">
              <div className="rh-overline">Order summary</div>
              <div className="max-h-64 overflow-y-auto space-y-3 border-y border-[#e5e5e5] py-3">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 text-sm">
                    <div className="w-12 h-12 bg-[#f5f5f5] flex-shrink-0"><img src={i.image} alt="" className="w-full h-full object-cover" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="line-clamp-1">{i.name}</div>
                      <div className="font-mono text-xs text-[#4a4a4a]">{i.quantity} × ${i.price.toFixed(2)}</div>
                    </div>
                    <div className="font-mono">${(i.price * i.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-sm"><span className="text-[#4a4a4a]">Subtotal</span><span className="rh-num">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#4a4a4a]">Shipping</span><span className="rh-num">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#4a4a4a]">Tax</span><span className="rh-num">${tax.toFixed(2)}</span></div>
              {coupon && <div className="flex justify-between text-sm text-[#008a00]" data-testid="discount-row"><span>Discount ({coupon.code})</span><span className="rh-num">-${discount.toFixed(2)}</span></div>}
              <div className="border-t border-[#0a0a0a] pt-3">
                <div className="rh-overline mb-2">Promo code</div>
                <div className="flex gap-2">
                  <input data-testid="coupon-input" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Enter code" className="flex-1 border border-[#0a0a0a] px-3 py-2 text-sm" />
                  <button type="button" onClick={applyCoupon} disabled={couponBusy} data-testid="apply-coupon-btn" className="rh-btn-secondary">Apply</button>
                </div>
              </div>
              <div className="border-t border-[#0a0a0a] pt-4 flex justify-between items-baseline">
                <span className="font-semibold">Total</span>
                <span className="font-display font-black text-2xl rh-num" data-testid="checkout-total">${total.toFixed(2)}</span>
              </div>
              <button data-testid="place-order-btn" type="submit" disabled={submitting || items.length === 0} className="rh-btn-primary w-full">
                {submitting ? "Redirecting to Stripe…" : "Pay with Stripe"}
              </button>
              {!user && <p className="text-xs text-[#4a4a4a] text-center">Checking out as guest — no account required.</p>}
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, className = "", testid }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">{label}{required && " *"}</span>
      <input data-testid={`field-${testid}`} type={type} required={required} value={value} onChange={onChange} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#002fa7]" />
    </label>
  );
}
